import { Box } from '@mui/material';
import { ReactNode } from 'react';
import { __t } from '../../../i18n/t';
import { ROW_ART } from '../../settings/HomeItemsInput/homeItems';
import { mix, rgba, luminance } from '../lib/appColors';

/**
 * As seções da Home do app desenhadas uma a uma, na cor da rede.
 *
 * Existem para dois lugares: o mockup do estúdio de arte, que mostra uma Home
 * de exemplo atrás da arte gerada, e a prévia do editor de Layout da Home, que
 * monta a MESMA tela na ordem que o operador configurou. Por isso o desenho não
 * sabe de onde vêm os dados: recebe uma lista pronta (`HomeSectionView[]`) e a
 * cor da marca.
 *
 * ESPAÇO DE MEDIDAS: pontos do iPhone, 390 de largura — o mesmo do `AppMockup`
 * e do app. Gutter de 15 e faixa de 360 saem do `HomeImageRow`/`HorizontalList`.
 *
 * Ilustração em SVG inline, e não foto: quadro tracejado dá um vazio que não
 * existe no app (todo brinde publicado tem foto), e imagem de outra origem
 * sujaria o canvas da captura do `MockupDialog`. São exemplos, não o catálogo
 * da rede — as únicas imagens de verdade aqui são os blocos que o operador
 * subiu, que são o conteúdo em si.
 */

const GUTTER = 15;
const CONTENT_W = 390 - GUTTER * 2;
const GAP = 10;

const INK = '#16161f';
const LABEL = '#61616A';
const MUTED = '#8E8E93';
const CARD_BG = '#F3F3F3';

/**
 * O que o desenho precisa saber de uma seção. `title` chega PRONTO: quem monta a
 * lista é quem sabe se aquilo é texto do operador, literal do build do app (que
 * é PT e não se traduz) ou exemplo do estúdio.
 */
export type HomeSectionView =
  | {
    kind: 'section'; id: string; title?: string | null;
    /** Arte real da seção, quando ela tem (cards do carrossel). Ausente = ilustração. */
    images?: { id: string; src: string }[];
  }
  | {
    kind: 'row'; id: string; title?: string | null;
    layout: 'full' | 'split';
    images: { id: string; src: string }[];
  };

// Içados por serem montados dentro de `.map()`: um objeto novo por item, por
// render, vira trabalho do `styleFunctionSx` e do emotion à toa.
const TITLE_SX = { mx: `${GUTTER}px`, mb: '10px', fontSize: 15, fontWeight: 700, color: LABEL };
const STRIP_SX = { display: 'flex', gap: '12px', px: `${GUTTER}px`, overflow: 'hidden' };
const CARD_SX = { backgroundColor: CARD_BG, borderRadius: '8px', flex: 'none' };
const SECTION_SX = { mb: '16px' };
const THUMB_LABEL_SX = { fontSize: 12, color: MUTED, mt: '5px', lineHeight: 1.22 };
const THUMB_SX = { width: 85, flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' };
const CATEGORY_CARD_SX = {
  ...CARD_SX, width: 85, height: 95, py: '15px',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between',
};
const CATEGORY_LABEL_SX = { fontSize: 11, color: MUTED, textAlign: 'center', px: '4px' };
const GIFT_ART_SX = { width: 85, height: 85, borderRadius: '8px', display: 'block', flex: 'none' };
const PLACE_SX = { width: 112, flex: 'none' };
const PLACE_ART_SX = { width: 112, height: 72, borderRadius: '8px', display: 'block' };
const FILLIN_ICON_SX = {
  width: 38, height: 38, borderRadius: '19px', flex: 'none',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const FILLIN_TEXT_SX = { flex: 1, minWidth: 0 };
const FILLIN_VALUE_SX = { fontSize: 13, fontWeight: 700, color: INK };
const FILLIN_SUB_SX = { fontSize: 11, color: MUTED, mt: '2px' };

/**
 * O traço das ilustrações de linha. Um componente só porque as três seções que o
 * usam (categoria, abastecimento, contato) só diferem no tamanho — e porque a
 * espessura tem que mudar nas três de uma vez.
 */
const LineIcon = ({ size, color, children }: { size: number; color: string; children: ReactNode }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color}
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

/** A mesma bomba aparece na categoria "Combustível" e no card de abastecimento. */
const PUMP_PATH = 'M4 20V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v14M3 20h12M14 9h3a2 2 0 0 1 2 2v5a1.5 1.5 0 0 0 3 0V9';

/* ---------------------------------------------------------------- categorias */

const CATEGORIES: [string, ReactNode][] = [
  ['Alimentos', <path key="a" d="M7 3v8m0 0v10M4.5 3v5a2.5 2.5 0 0 0 5 0V3M17.5 3c-1.5 2-2 4-2 6.5 0 1.5.8 2.5 2 2.5v9" />],
  ['Combustível', <path key="b" d={PUMP_PATH} />],
  ['Troca de óleo', <path key="c" d="M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9Z" />],
  ['Lava-jato', <path key="d" d="M5 13l1.5-4.5A2 2 0 0 1 8.4 7h7.2a2 2 0 0 1 1.9 1.5L19 13M4 13h16v4H4z" />],
];

const Categories = ({ brand }: { brand: string }) => (
  <Box sx={STRIP_SX}>
    {CATEGORIES.map(([label, path]) => (
      <Box key={label} sx={CATEGORY_CARD_SX}>
        <LineIcon size={40} color={brand}>{path}</LineIcon>
        <Box sx={CATEGORY_LABEL_SX}>{__t(label)}</Box>
      </Box>
    ))}
  </Box>
);

/* -------------------------------------------------------------------- brindes */

/**
 * Os tons das miniaturas de brinde, derivados da cor da rede.
 *
 * `tint` é o fundo e `ink` é o desenho: eles precisam ficar longe um do outro
 * em QUALQUER marca, e é por isso que a cor crua não serve nos dois. Marca
 * clara (amarelo, branco) desapareceria no fundo claro, então o desenho entra
 * numa versão escurecida dela.
 */
const giftPalette = (brand: string) => {
  const ink = luminance(brand) > 0.32 ? mix(brand, '#16161F', 0.58) : brand;

  // O fundo sai da marca CRUA, e não do `ink`, para o matiz da rede aparecer no
  // maior pedaço da miniatura — amarelo continua amarelo ali. O piso é para
  // marca clara: diluída em branco ela CHEGA a branco, e o quadro sumiria
  // dentro do card, que também é branco.
  const washed = mix(brand, '#FFFFFF', 0.88);

  return {
    tint: luminance(washed) > 0.92 ? mix(washed, '#8E9AA8', 0.3) : washed,
    ink,
    /** Peça secundária: separa do `ink` sem sumir no `tint`. */
    soft: mix(ink, '#FFFFFF', 0.6),
    /** Sulco do pneu — precisa aparecer POR DENTRO do `ink`. */
    groove: mix(ink, '#FFFFFF', 0.22),
    /** Roda: o que fica mais escuro que o corpo. */
    shade: mix(ink, '#16161F', 0.45),
  };
};

type GiftColors = ReturnType<typeof giftPalette>;

/**
 * A chave é `name`, e não `label`, de propósito: o plugin de i18n embrulha
 * `label:` dentro do objeto, o que aconteceria aqui no escopo do módulo — e
 * constante de módulo congela no idioma do primeiro import. Traduzir na
 * renderização (`__t(name)`) acompanha a troca de idioma.
 */
const GIFTS: { name: string; art: (c: GiftColors) => ReactNode }[] = [
  {
    name: 'Troca de óleo',
    art: c => (
      <>
        <rect width="64" height="64" fill={c.tint} />
        <rect x="26" y="11" width="12" height="7" rx="2" fill={c.soft} />
        <path d="M28 17h8v9h-8z" fill={c.ink} />
        <rect x="17" y="25" width="30" height="27" rx="5" fill={c.ink} />
        <rect x="22" y="32" width="20" height="13" rx="2.5" fill="#FFF" />
        <path d="M26 36.5h12M26 40.5h8" stroke={c.ink} strokeWidth="2" strokeLinecap="round" />
      </>
    ),
  },
  {
    name: 'Café expresso',
    art: c => (
      <>
        <rect width="64" height="64" fill={c.tint} />
        <path d="M26 15c2.5-3-2-5.5.5-9M35 15c2.5-3-2-5.5.5-9" stroke={c.soft} strokeWidth="2.6" strokeLinecap="round" fill="none" />
        <path d="M42 29h4a6.5 6.5 0 0 1 0 13h-4" stroke={c.ink} strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d="M15 24h28v12a12 12 0 0 1-12 12h-4a12 12 0 0 1-12-12V24Z" fill={c.ink} />
        {/* A boca da xícara: é o vazado branco que faz a peça ler como xícara
            cheia, e não como um bloco. */}
        <rect x="17.5" y="26.5" width="23" height="3.6" fill="#FFF" opacity=".92" />
        <rect x="10" y="49.5" width="38" height="5" rx="2.5" fill={c.ink} />
      </>
    ),
  },
  {
    name: 'Lavagem simples',
    art: c => (
      <>
        <rect width="64" height="64" fill={c.tint} />
        <circle cx="15" cy="14" r="5.5" fill="#FFF" opacity=".95" />
        <circle cx="27" cy="8" r="3.5" fill="#FFF" opacity=".8" />
        <circle cx="49" cy="13" r="4.5" fill="#FFF" opacity=".85" />
        <path d="M14 42v-4l4.4-10A4.5 4.5 0 0 1 22.5 25h19a4.5 4.5 0 0 1 4.1 3l4.4 10v4Z" fill={c.ink} />
        <path d="M22.5 29h19l2.8 7H19.7Z" fill="#FFF" opacity=".9" />
        <rect x="11" y="38" width="42" height="9" rx="3.5" fill={c.ink} />
        <circle cx="20" cy="48" r="4" fill={c.shade} />
        <circle cx="44" cy="48" r="4" fill={c.shade} />
      </>
    ),
  },
  {
    name: 'Calibragem',
    art: c => (
      <>
        <rect width="64" height="64" fill={c.tint} />
        <circle cx="29" cy="31" r="19" fill={c.ink} />
        <circle cx="29" cy="31" r="16.5" fill="none" stroke={c.groove} strokeWidth="4" strokeDasharray="3 4.2" />
        <circle cx="29" cy="31" r="10" fill="#FFF" />
        <circle cx="29" cy="31" r="4" fill={c.soft} />
        <circle cx="46" cy="47" r="12" fill="#FFF" stroke={c.soft} strokeWidth="2" />
        <path d="M46 47l6-5" stroke={c.ink} strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="46" cy="47" r="2" fill={c.ink} />
      </>
    ),
  },
];

const Gifts = ({ brand }: { brand: string }) => {
  const colors = giftPalette(brand);

  return (
    <Box sx={STRIP_SX}>
      {GIFTS.map(({ name, art }) => (
        <Box key={name} sx={THUMB_SX}>
          <Box component="svg" viewBox="0 0 64 64" sx={GIFT_ART_SX}>{art(colors)}</Box>
          <Box sx={{ ...THUMB_LABEL_SX, textAlign: 'center' }}>{__t(name)}</Box>
        </Box>
      ))}
    </Box>
  );
};

/* --------------------------------------------------------------------- locais */

const PLACES = ['Posto Centro', 'Posto Marginal', 'Posto Litoral'];

const Places = ({ brand }: { brand: string }) => {
  const colors = giftPalette(brand);

  return (
    <Box sx={STRIP_SX}>
      {PLACES.map(label => (
        <Box key={label} sx={PLACE_SX}>
          <Box component="svg" viewBox="0 0 112 72" sx={PLACE_ART_SX}>
            <rect width="112" height="72" fill={colors.tint} />
            {/* cobertura e pista: o suficiente para ler "posto" de relance */}
            <path d="M18 30h44v34H18z" fill={colors.ink} />
            <path d="M12 30 40 16l28 14Z" fill={colors.soft} />
            <rect x="28" y="40" width="24" height="16" rx="2" fill="#FFF" opacity=".92" />
            <rect x="74" y="36" width="18" height="28" rx="4" fill={colors.ink} />
            <rect x="78" y="42" width="10" height="7" rx="1.5" fill="#FFF" opacity=".9" />
            <rect x="8" y="64" width="96" height="4" rx="2" fill={colors.shade} />
          </Box>
          <Box sx={THUMB_LABEL_SX}>{__t(label)}</Box>
        </Box>
      ))}
    </Box>
  );
};

/* -------------------------------------------------------------- abastecimentos */

const FILLINS = [
  { place: 'Posto Centro', date: '12/10', fuel: 'Gasolina comum', value: 'R$ 150,00', points: '+150 pts' },
  { place: 'Posto Litoral', date: '28/09', fuel: 'Etanol', value: 'R$ 90,00', points: '+90 pts' },
];

const Fillins = ({ brand }: { brand: string }) => (
  <Box sx={{ px: `${GUTTER}px`, display: 'flex', flexDirection: 'column', gap: `${GAP}px` }}>
    {FILLINS.map(fillin => {
      // Montado fora do JSX: template literal em posição de texto vira chave de
      // catálogo, e "%{arg1} · %{arg2}" não é texto para traduzir.
      const subtitle = `${fillin.date} · ${__t(fillin.fuel)}`;

      return (
      <Box key={fillin.place} sx={{
        ...CARD_SX, width: CONTENT_W, p: '12px',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <Box sx={{ ...FILLIN_ICON_SX, backgroundColor: rgba(brand, 0.12) }}>
          <LineIcon size={22} color={brand}><path d={PUMP_PATH} /></LineIcon>
        </Box>
        <Box sx={FILLIN_TEXT_SX}>
          <Box sx={FILLIN_VALUE_SX}>{__t(fillin.place)}</Box>
          <Box sx={FILLIN_SUB_SX}>{subtitle}</Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={FILLIN_VALUE_SX}>{fillin.value}</Box>
          <Box sx={{ fontSize: 11, color: brand, fontWeight: 600, mt: '2px' }}>{fillin.points}</Box>
        </Box>
      </Box>
      );
    })}
  </Box>
);

/* -------------------------------------------------------------------- contato */

const CONTACTS: [string, ReactNode][] = [
  ['Whatsapp', <path key="w" d="M4 20l1.3-3.9A7.5 7.5 0 1 1 8 19.1Z" />],
  ['E-mail', <><path key="e1" d="M3 6h18v12H3z" /><path key="e2" d="m3 7 9 6 9-6" /></>],
  ['Mensagem', <path key="m" d="M4 5h16v11H8l-4 3Z" />],
];

const Contact = ({ brand }: { brand: string }) => (
  <Box sx={{ display: 'flex', gap: `${GAP}px`, px: `${GUTTER}px` }}>
    {CONTACTS.map(([label, path]) => (
      <Box key={label} sx={{
        flex: 1, height: 56, borderRadius: '8px', border: `1px solid ${rgba(brand, 0.35)}`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
      }}>
        <LineIcon size={22} color={brand}>{path}</LineIcon>
        <Box sx={{ fontSize: 11, color: LABEL }}>{__t(label)}</Box>
      </Box>
    ))}
  </Box>
);

/* ------------------------------------------------------------------ pendências */

/** Fora do JSX: valor de exemplo não é texto para traduzir. */
const PAYMENT_AMOUNT = 'R$ 180,00';

const Payments = ({ brand }: { brand: string }) => (
  <Box sx={{ px: `${GUTTER}px` }}>
    <Box sx={{
      width: CONTENT_W, borderRadius: '8px', p: '12px',
      border: `1px solid ${rgba(brand, 0.3)}`, backgroundColor: rgba(brand, 0.06),
    }}>
      <Box sx={{ fontSize: 12, color: LABEL }}>Pagamento pendente</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '6px' }}>
        <Box sx={{ fontSize: 17, fontWeight: 800, color: INK }}>{PAYMENT_AMOUNT}</Box>
        <Box sx={{
          px: '14px', py: '7px', borderRadius: '16px', backgroundColor: brand,
          color: '#fff', fontSize: 12, fontWeight: 700,
        }}>Pagar agora</Box>
      </Box>
    </Box>
  </Box>
);

/* --------------------------------------------------------------------- roleta */

const Roleta = ({ brand }: { brand: string }) => {
  const soft = mix(brand, '#FFFFFF', 0.72);

  return (
    <Box sx={{ px: `${GUTTER}px` }}>
      <Box sx={{
        width: CONTENT_W, borderRadius: '8px', p: '12px', color: '#fff',
        display: 'flex', alignItems: 'center', gap: '12px',
        background: `linear-gradient(115deg, ${rgba(brand, 0.85)} 0%, ${brand} 100%)`,
      }}>
        <Box sx={{
          width: 46, height: 46, borderRadius: '23px', flex: 'none',
          border: '3px solid rgba(255,255,255,.9)',
          background: `conic-gradient(#fff 0 60deg, ${soft} 60deg 120deg, #fff 120deg 180deg, ${soft} 180deg 240deg, #fff 240deg 300deg, ${soft} 300deg 360deg)`,
        }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ fontSize: 15, fontWeight: 800 }}>Você tem 2 giros</Box>
          <Box sx={{ fontSize: 12, opacity: .9, mt: '2px' }}>Gire a roleta e concorra a brindes</Box>
        </Box>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------- carrossel */

// 321 × 146 é o 11:5 do `ImageCarousel` do app. Içados: ficam dentro de ternário no
// JSX, onde o plugin de i18n embrulharia os valores.
const CAROUSEL_SLIDE_SX = { ml: `${GUTTER}px`, width: 321, height: 146, borderRadius: '8px' };
const CAROUSEL_IMG_SX = { ...CAROUSEL_SLIDE_SX, objectFit: 'cover', display: 'block' };
const CAROUSEL_ART_SX = {
  ...CAROUSEL_SLIDE_SX, overflow: 'hidden', display: 'flex', alignItems: 'center', color: '#fff',
};
const CAROUSEL_ART_TEXT_SX = { px: '18px' };
const CAROUSEL_ART_TITLE_SX = { fontSize: 19, fontWeight: 800, lineHeight: 1.12 };
const CAROUSEL_ART_SUBTITLE_SX = { fontSize: 15, opacity: .92, mt: '3px' };
const carouselGradient = (brand: string) =>
  `linear-gradient(115deg, ${rgba(brand, 0.78)} 0%, ${brand} 46%, ${rgba(brand, 0.9)} 100%)`;

const Carousel = ({ brand, images }: { brand: string; images?: { id: string; src: string }[] }) => (
  <>
    {images?.length ? (
      <Box component="img" src={images[0].src} alt="" sx={CAROUSEL_IMG_SX} />
    ) : (
      <Box sx={{ ...CAROUSEL_ART_SX, background: carouselGradient(brand) }}>
        <Box sx={CAROUSEL_ART_TEXT_SX}>
          <Box sx={CAROUSEL_ART_TITLE_SX}>Abasteceu, pontuou.</Box>
          <Box sx={CAROUSEL_ART_SUBTITLE_SX}>Troque seus pontos por brindes</Box>
        </Box>
      </Box>
    )}
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: '9px' }}>
      {(images?.length ? images.map((_, i) => i) : [0, 1, 2]).map(i => (
        <Box key={i} sx={{
          width: 6, height: 6, borderRadius: '3px', mx: '3px',
          backgroundColor: i === 0 ? LABEL : '#C7C7CC',
        }} />
      ))}
    </Box>
  </>
);

/* ------------------------------------------------------------------------ cola */

/** Qual desenho cada seção do build usa. Id de fora da lista não desenha nada. */
type SectionArtProps = { brand: string; images?: { id: string; src: string }[] };

const SECTION_ART: Record<string, (props: SectionArtProps) => ReactNode> = {
  payments: Payments,
  categories: Categories,
  roleta: Roleta,
  carousel: Carousel,
  redeemed: Gifts,
  visited: Places,
  lastFillins: Fillins,
  contact: Contact,
};

const ImageRow = ({ layout, images }: { layout: 'full' | 'split'; images: { id: string; src: string }[] }) => {
  // Bloco sozinho numa linha `split` fica em meia largura, como no app: esticar
  // deformaria uma arte feita em 5:3.
  const width = layout === 'split' ? (CONTENT_W - GAP) / 2 : CONTENT_W;
  const { ratio } = ROW_ART[layout];

  return (
    <Box sx={{ display: 'flex', gap: `${GAP}px`, px: `${GUTTER}px` }}>
      {images.map(image => (
        <Box key={image.id} component="img" src={image.src} alt="" sx={{
          width, height: width / ratio, objectFit: 'cover', borderRadius: '8px', display: 'block',
        }} />
      ))}
    </Box>
  );
};

/**
 * A Home de exemplo do estúdio de arte: a mesma tela de sempre, agora como lista.
 *
 * É função, e não constante, porque estes títulos são EXEMPLO escrito pelo admin
 * — se traduzem — e `__t` em constante de módulo congelaria no idioma do primeiro
 * import. Os títulos que vêm do editor são o oposto: literal do app, PT, crus.
 */
export const demoHome = (): HomeSectionView[] => [
  { kind: 'section', id: 'categories', title: __t('Categorias') },
  { kind: 'section', id: 'carousel' },
  { kind: 'section', id: 'redeemed', title: __t('Brindes em destaque') },
];

const HomeBody = ({ items, brand }: { items: HomeSectionView[]; brand: string }) => (
  <>
    {items.map(item => {
      if (item.kind === 'row') {
        return (
          <Box key={item.id} sx={SECTION_SX}>
            {!!item.title && <Box sx={TITLE_SX}>{item.title}</Box>}
            <ImageRow layout={item.layout} images={item.images} />
          </Box>
        );
      }

      const Art = SECTION_ART[item.id];
      if (!Art) { return null; }

      return (
        <Box key={item.id} sx={SECTION_SX}>
          {!!item.title && <Box sx={TITLE_SX}>{item.title}</Box>}
          <Art brand={brand} images={item.images} />
        </Box>
      );
    })}
  </>
);

export default HomeBody;
